import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, ChevronDown, Briefcase, Menu, X, Home, Info, Phone, Users, Bell, Lightbulb, GraduationCap, Building2, Heart, Leaf, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { getUser, getToken, clearAuth } from '@/utils/auth';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const isLoggedIn = !!getToken();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
    window.location.reload();
  };

  const getDashboardPath = () => {
    if (!user) return '/';
    switch(user.role) {
      case 'job_seeker': return '/job-seeker/dashboard';
      case 'employer': return '/employer/dashboard';
      case 'hr': return '/employer/dashboard';
      case 'admin': return '/admin/dashboard';
      default: return '/';
    }
  };

  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    setDesktopMenuOpen(false);
    navigate(path);
  };

  const servicesMenu = [
    { icon: Bell, label: 'Job Notification', path: '/job-notification' },
    { icon: Lightbulb, label: 'Entrepreneurship', path: '/entrepreneurship' },
    { icon: GraduationCap, label: 'Career Guidance', path: '/career-guidance' },
    { icon: Award, label: 'Job Fairs', path: '/job-fairs' },
    { icon: Building2, label: 'Job Facilitation Center', path: '/job-facilitation-center' },
    { icon: GraduationCap, label: 'Education Support', path: '/education-support' },
    { icon: Heart, label: 'Medical Support', path: '/medical-support' },
    { icon: Leaf, label: 'Environment & Disaster Support', path: '/environment-disaster-support' },
  ];

  const mainNavItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Info, label: 'About', path: '/about' },
    { icon: Award, label: 'Achievements', path: '/achievements' },
    { icon: Users, label: 'Our Clients', path: '/our-clients' },
    { icon: Users, label: 'Team', path: '/team' },
    { icon: Phone, label: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-10 md:h-12 lg:h-14 w-auto object-contain" />
          </div>

          {/* Desktop Auth Buttons + Hamburger Menu */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="my-account-btn" variant="outline" className="border-teal-500 text-teal-700 hover:bg-teal-50">
                    <User className="w-4 h-4 mr-2" />
                    My Account
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    data-testid="go-to-dashboard-btn"
                    onClick={() => navigate(getDashboardPath())}
                    className="cursor-pointer"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Go to Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button data-testid="header-about-btn" variant="ghost" onClick={() => navigate('/about')} className="border border-emerald-200 bg-emerald-50/70 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300">
                  <Info className="w-4 h-4 mr-1.5" />
                  About Us
                </Button>
                <Button data-testid="job-seeker-login-btn" variant="outline" onClick={() => navigate('/job-seeker/login')} className="border-slate-300 hover:bg-slate-50">
                  Job Seeker Login
                </Button>
                <Button data-testid="employer-login-btn" onClick={() => navigate('/employer/login')} className="bg-teal-600 hover:bg-teal-700 text-white">
                  Employer Login
                </Button>
              </>
            )}

            {/* Desktop Hamburger Menu */}
            <Sheet open={desktopMenuOpen} onOpenChange={setDesktopMenuOpen}>
              <SheetTrigger asChild>
                <Button data-testid="desktop-menu-btn" variant="outline" size="icon" className="border-slate-300 hover:bg-slate-50">
                  <Menu className="w-5 h-5 text-slate-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0 overflow-y-auto">
                <div className="flex flex-col h-full">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-slate-100">
                    <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-12 w-auto object-contain" />
                  </div>
                  
                  {/* Navigation */}
                  <nav className="flex-1 p-4">
                    {/* Main Nav Items */}
                    <div className="space-y-1 mb-6">
                      {mainNavItems.map((item) => (
                        <Button
                          key={item.path}
                          variant="ghost"
                          className={`w-full justify-start h-11 ${isActive(item.path) ? 'text-teal-600 bg-teal-50' : ''}`}
                          onClick={() => handleNavigation(item.path)}
                        >
                          <item.icon className="w-5 h-5 mr-3" />
                          {item.label}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Services Section */}
                    <div className="border-t border-slate-100 pt-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
                        Our Services
                      </p>
                      <div className="space-y-1">
                        {servicesMenu.map((service) => (
                          <Button
                            key={service.path}
                            variant="ghost"
                            className={`w-full justify-start h-10 text-sm ${isActive(service.path) ? 'text-teal-600 bg-teal-50' : ''}`}
                            onClick={() => handleNavigation(service.path)}
                          >
                            <service.icon className="w-4 h-4 mr-3 text-teal-600" />
                            {service.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </nav>
                  
                  {/* Footer */}
                  <div className="p-4 border-t border-slate-200 bg-slate-50">
                    <p className="text-xs text-slate-500 text-center">
                      © 2025 VVVSI. All rights reserved.
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {isLoggedIn && user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="mobile-account-btn" variant="ghost" size="icon" className="text-teal-700">
                    <User className="w-6 h-6" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{user.name || 'User'}</p>
                    <p className="text-xs text-slate-500">{user.mobile}</p>
                  </div>
                  <DropdownMenuItem 
                    onClick={() => navigate(getDashboardPath())}
                    className="cursor-pointer"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Go to Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button data-testid="mobile-menu-btn" variant="ghost" size="icon">
                  <Menu className="w-6 h-6 text-slate-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0 overflow-y-auto">
                <div className="flex flex-col h-full">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-slate-100">
                    <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-12 w-auto object-contain" />
                  </div>
                  
                  {/* Navigation */}
                  <nav className="flex-1 p-4">
                    {/* Login Buttons FIRST for non-logged in users */}
                    {!isLoggedIn && (
                      <div className="mb-6 space-y-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
                          Login / Register
                        </p>
                        <Button 
                          variant="outline" 
                          className="w-full justify-start h-11"
                          onClick={() => handleNavigation('/job-seeker/login')}
                        >
                          <User className="w-5 h-5 mr-3 text-teal-600" />
                          Job Seeker Login / Register
                        </Button>
                        <Button 
                          className="w-full justify-start h-11 bg-teal-600 hover:bg-teal-700"
                          onClick={() => handleNavigation('/employer/login')}
                        >
                          <Briefcase className="w-5 h-5 mr-3" />
                          Employer Login / Register
                        </Button>
                        <div className="border-t border-slate-100 mt-4" />
                      </div>
                    )}

                    {/* Main Nav Items */}
                    <div className="space-y-1 mb-6">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
                        Menu
                      </p>
                      {mainNavItems.map((item) => (
                        <Button
                          key={item.path}
                          variant="ghost"
                          className={`w-full justify-start h-11 ${isActive(item.path) ? 'text-teal-600 bg-teal-50' : ''}`}
                          onClick={() => handleNavigation(item.path)}
                        >
                          <item.icon className="w-5 h-5 mr-3" />
                          {item.label}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Services Section */}
                    <div className="border-t border-slate-100 pt-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
                        Our Services
                      </p>
                      <div className="space-y-1">
                        {servicesMenu.map((service) => (
                          <Button
                            key={service.path}
                            variant="ghost"
                            className={`w-full justify-start h-10 text-sm ${isActive(service.path) ? 'text-teal-600 bg-teal-50' : ''}`}
                            onClick={() => handleNavigation(service.path)}
                          >
                            <service.icon className="w-4 h-4 mr-3 text-teal-600" />
                            {service.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Donate Button */}
                    <div className="border-t border-slate-100 pt-4 mt-4">
                      <Button 
                        className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold shadow-lg h-11"
                        onClick={() => handleNavigation('/donate')}
                      >
                        <Heart className="w-5 h-5 mr-2" />
                        Donate Us
                      </Button>
                    </div>
                  </nav>
                  
                  {/* Footer */}
                  <div className="p-4 border-t border-slate-200 bg-slate-50">
                    <p className="text-xs text-slate-500 text-center">
                      © 2025 VVVSI. All rights reserved.
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
